import { useCallback } from 'react';
import { useLocation } from '@direct-frontend/modules-router';
import { useExtendSearchParams } from '../../hooks';
import { type PreviewId } from '../types';

type Returns<Data extends unknown = unknown> = {
    isOpen: boolean;
    id: PreviewId | undefined;
    entityId: string | undefined;
    data: Data | undefined;
    setOpen: (id: PreviewId, previewData?: Data) => void;
    /**
     * Закрыть виджет предпросмотра
     * @param isReplaced - заменить ли запись в истории при закрытии (по умолчанию (false) будет новая запись в истории)
     *                     если поставить true, то будет заменена текущая запись в истории
     *
     */
    setClose: (isReplaced?: boolean) => void;
    setPreviewData: (previewData?: Data) => void;
};

function isPreviewId(id: string | null): id is PreviewId {
    if (!id) {
        return false;
    }

    return id.split('/').length === 2;
}

/*
 * Хук для управления виджетом предпросмотра
 */
export function usePreviewWidget<Data extends unknown = unknown>(): Returns<Data> {
    const { searchParams, setSearchParams } = useExtendSearchParams();
    const { state } = useLocation();

    const isOpen = searchParams.has('preview');
    const id = searchParams.get('preview');
    const data = (state && typeof state === 'object' && 'previewData' in state ? state.previewData : undefined) as Data;

    const setOpen = useCallback(
        (id: PreviewId, previewData?: Data) => {
            setSearchParams((newSearchParams) => {
                if (newSearchParams.get('preview') === id) {
                    return [newSearchParams];
                }

                newSearchParams.set('preview', id);

                return [newSearchParams, { state: { previewData: previewData }, replace: false }];
            });
        },
        [setSearchParams]
    );

    const setClose = useCallback(
        (isReplaced: boolean = false) => {
            setSearchParams((newSearchParams) => {
                newSearchParams.delete('preview');

                return [newSearchParams, { replace: isReplaced }];
            });
        },
        [setSearchParams]
    );

    const setPreviewData = useCallback(
        (previewData?: Data) => {
            setSearchParams((newSearchParams) => {
                const locationState = state && typeof state === 'object' ? state : {};

                return [newSearchParams, { state: { ...locationState, previewData }, replace: true }];
            });
        },
        [setSearchParams, state]
    );

    const isId = isPreviewId(id);
    const entityId = isId ? id.split('/')[1] : undefined;

    return { isOpen, id: isId ? id : undefined, data, entityId, setOpen, setClose, setPreviewData };
}
